import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import validator from 'validator'

import {
    addUser,
    updateUser,
} from '../../../features/users/usersSlice'

import './UserForm.css'
import CTextField from '../../../components/UI/CTextField'
import CButton from '../../../components/UI/CButton'
import CSelect from '../../../components/UI/CSelect'
import CNumericField from '../../../components/UI/CNumericField'

/**
 * Reusable component for adding or editing users
 * Handles all user form logic internally
 */
const UserForm = ({
    user = null, // If provided, form is in edit mode
    onClose,
    onSuccess,
    onError,
}) => {
    const dispatch = useDispatch()
    const { loading } = useSelector((state) => state.users)

    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        email: '',
        idNumber: '',
        mobileNumber: '',
        status: { value: 'Active', label: 'Active' },
    })
    const [validationErrors, setValidationErrors] = useState({})

    const isEditMode = !!user

    // Initialize form with user data if editing
    useEffect(() => {
        if (user) {
            setFormData({
                name: user.firstnames || '',
                surname: user.surname || '',
                email: user.email || '',
                idNumber: user.idnumber || '',
                mobileNumber: user.cell || '',
                status: user.isactive ? { value: 'Active', label: 'Active' } : { value: 'Inactive', label: 'Inactive' },
            })
        }
    }, [user])

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }))
        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev }
                delete newErrors[field]
                return newErrors
            })
        }
    }

    const validateForm = () => {
        const errors = {}

        if (validator.isEmpty(formData.name.trim())) {
            errors.name = 'Name is required'
        }

        if (!formData.surname || validator.isEmpty(formData.surname.trim())) {
            errors.surname = 'Surname is required'
        }

        if (validator.isEmpty(formData.email.trim())) {
            errors.email = 'Email is required'
        } else if (!validator.isEmail(formData.email)) {
            errors.email = 'Invalid email format'
        }

        if (validator.isEmpty(formData.idNumber.trim())) {
            errors.idNumber = 'ID number is required'
        }

        if (validator.isEmpty(formData.mobileNumber.trim())) {
            errors.mobileNumber = 'Mobile number is required'
        } else if (formData.mobileNumber.trim().length < 10) {
            errors.mobileNumber = 'Mobile number must be 10 digits'
        } else if (!validator.isMobilePhone(formData.mobileNumber, 'any')) {
            errors.mobileNumber = 'Invalid mobile number format'
        }

        setValidationErrors(errors)
        return Object.keys(errors).length === 0
    }

    const handleCancel = () => {
        setFormData({ name: '', surname: '', email: '', idNumber: '', mobileNumber: '', status: { value: 'Active', label: 'Active' } })
        setValidationErrors({})
        if (onClose) onClose()
    }

    const handleSubmit = async () => {
        if (!validateForm()) {
            return
        }

        const data = {
            firstnames: formData.name.trim(),
            surname: formData.surname.trim(),
            email: formData.email.trim(),
            idnumber: formData.idNumber.trim(),
            cell: formData.mobileNumber.trim(),
            isactive: formData.status.value === 'Active'
        }

        try {
            if (isEditMode) {
                await dispatch(updateUser({ id: user.participantid, userData: data })).unwrap()
            } else {
                await dispatch(addUser(data)).unwrap()
            }

            // Reset form
            setFormData({ name: '', surname: '', email: '', idNumber: '', mobileNumber: '', status: { value: 'Active', label: 'Active' } })
            setValidationErrors({})

            if (onSuccess) onSuccess(formData)
            if (onClose) onClose()
        } catch (err) {
            if (onError) onError(err)
        }
    }

    return (
        <div className="user-form">
            <span className="text-4">Enter user details below.</span>

            <CTextField
                label="Name"
                type="text"
                value={formData.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder="Enter user name"
                fullWidth
                required
                autoFocus
                noNumeric
                error={!!validationErrors.name}
                helperText={validationErrors.name || ''}
                disabled={loading.add || loading.update}
            />
            <CTextField
                label="Surname"
                type="text"
                value={formData.surname}
                onChange={(e) => handleFieldChange('surname', e.target.value)}
                placeholder="Enter surname"
                fullWidth
                required
                noNumeric
                error={!!validationErrors.surname}
                helperText={validationErrors.surname || ''}
                disabled={loading.add || loading.update}
            />
            <CTextField
                label="Email address"
                type="email"
                value={formData.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                placeholder="Enter email address"
                fullWidth
                required
                error={!!validationErrors.email}
                helperText={validationErrors.email || ''}
                disabled={loading.add || loading.update}
            />
            <CTextField
                label="ID number"
                type="text"
                value={formData.idNumber}
                onChange={(e) => handleFieldChange('idNumber', e.target.value)}
                placeholder="Enter ID number"
                fullWidth
                required
                error={!!validationErrors.idNumber}
                helperText={validationErrors.idNumber || ''}
                disabled={loading.add || loading.update}
            />
            <CNumericField
                label="Mobile number"
                plainNumber
                required
                allowLeadingZeros
                maxLength={10}
                minLength={10}
                loadValue={formData.mobileNumber}
                onChange={(val) => handleFieldChange('mobileNumber', val)}
                helperText={validationErrors.mobileNumber || ''}
                isError={!!validationErrors.mobileNumber}
                disabled={loading.add || loading.update}
            />

            <CSelect
                label="Status"
                value={formData.status?.value}
                options={[
                    { value: 'Active', label: 'Active' },
                    { value: 'Inactive', label: 'Inactive' }
                ]}
                type={"Generic"}
                onChange={(value) => handleFieldChange('status', value)}
            ></CSelect>

            <div className="form-actions">
                <CButton
                    label="Cancel"
                    onClick={handleCancel}
                    variant="outlined"
                    color="secondary"
                    disabled={loading.add || loading.update}
                />
                <CButton
                    label={isEditMode ? 'Update' : 'Save'}
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    loading={loading.add || loading.update}
                />
            </div>
        </div>
    )
}

export default UserForm
